import React, { useState, useEffect } from 'react';
import TickerSlider from './TickerSlider';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Grid,
  Link,
  Alert,
  Autocomplete,
  Fade,
  Grow,
  Slide,
  Zoom,
  IconButton,
  Chip,
  LinearProgress,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import { 
  Person,
  Email,
  Lock,
  Business,
  Language,
  Description,
  Phone,
  LocationOn,
  Category,
  ArrowForward,
  ArrowBack,
  Check,
  Visibility,
  VisibilityOff,
  CheckCircle,
  RadioButtonUnchecked,
  FiberManualRecord,
} from '@mui/icons-material';
import httpClient from 'src/libs/axios';
import { API_ROUTES } from 'src/constants/routes';
import { PhotographMapper } from 'src/middleware/Photograph.middleware';
import UserService from 'src/services/user.services';
import { parseCookies } from 'nookies';
import { useSearchParams } from 'react-router-dom';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import { PhoneVerification } from 'src/components/PhoneVerification';
import { motion, AnimatePresence } from 'framer-motion';
import {
  saveRegistrationData,
  loadRegistrationData,
  clearRegistrationData,
  determineCurrentStep,
  hasCompletedInitialRegistration,
} from 'src/utils/registrationPersistence';

const SERVICE_INDUSTRIES = [
  'Photography', 'Real Estate', 'Healthcare', 'Legal Services',
  'Financial Services', 'Education', 'Consulting', 'Marketing',
  'IT Services', 'Construction', 'Retail', 'Hospitality',
  'Transportation', 'Manufacturing', 'Agriculture', 'Energy',
  'Entertainment', 'Non-Profit', 'Government', 'Other'
];

const STEP_TITLES = ['Get Started', 'Your Details', 'Contact Info'];

interface FormValues {
  // Step 1
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  termOfUse: boolean;
  
  // Step 2
  companyName: string;
  industry: string;
  website: string;
  
  // Step 3
  phone: string;
  phone_verification: string;
  location: any;
  timezone: string;
  category: string;
}

interface MultiStepRegistrationProps {
  onSuccess?: (data: any) => void;
  onToggleSignIn?: () => void;
}

const MultiStepRegistration: React.FC<MultiStepRegistrationProps> = ({ onSuccess, onToggleSignIn }) => {
  // Load saved registration data and determine initial state
  const savedData = loadRegistrationData();
  const initialState = determineCurrentStep(savedData);
  
  const [activeStep, setActiveStep] = useState(initialState.activeStep);
  const [isQuickStart, setIsQuickStart] = useState(initialState.isQuickStart);
  const [currentField, setCurrentField] = useState(initialState.currentField);
  const [formValues, setFormValues] = useState<Partial<FormValues>>(savedData);
  const [isPhoneVerified, setIsPhoneVerified] = useState(savedData.isPhoneVerified || false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('xs'));
  const [searchParams] = useSearchParams();
  
  // Save form values to persistence whenever they change
  useEffect(() => {
    if (Object.keys(formValues).length > 0) {
      saveRegistrationData({
        ...formValues,
        activeStep,
        currentField,
        isQuickStart,
        isPhoneVerified,
      });
    }
  }, [formValues, activeStep, currentField, isQuickStart, isPhoneVerified]);
  
  // Check if we should skip to a later step based on existing authentication
  useEffect(() => {
    if (hasCompletedInitialRegistration() && activeStep === 0) {
      // If user already has access token, skip to step 1 (company details)
      setActiveStep(1);
      setIsQuickStart(false);
    }
  }, []);
  
  // Auto-dismiss phone error after 7 seconds
  useEffect(() => {
    if (phoneError) {
      const timer = setTimeout(() => {
        setPhoneError(null);
      }, 7000);
      
      return () => clearTimeout(timer);
    }
  }, [phoneError]);

  const validationSchemas = [
    // Step 0 - Quick Start or Full Registration Form
    isQuickStart 
      ? Yup.object({
          website: Yup.string()
            .matches(
              /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/,
              'Please enter a valid URL'
            ),
        })
      : currentField === 0 ? Yup.object({ email: Yup.string().email('Invalid email').required('Email is required') })
        : currentField === 1 ? Yup.object({ firstName: Yup.string().required('First name is required') })
        : currentField === 2 ? Yup.object({ lastName: Yup.string().required('Last name is required') })
        : currentField === 3 ? Yup.object({ password: Yup.string().min(8, 'Minimum 8 characters').required('Required') })
        : Yup.object({ termOfUse: Yup.boolean().oneOf([true], 'You must accept the terms') }),
    // Step 1 - Company Details  
    Yup.object({
      companyName: Yup.string().required('Company name is required'),
      industry: Yup.string().required('Please select your industry'),
      website: Yup.string()
        .matches(
          /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/,
          'Please enter a valid URL'
        )
        .required('Website is required'),
    }),
    // Step 2 - Contact & Location
    Yup.object({
      phone: Yup.string().required('Phone number is required'),
      location: Yup.object().nullable(),
    }),
  ];

  const initialValues: FormValues = {
    // Step 1
    email: formValues.email || '',
    password: formValues.password || '',
    firstName: formValues.firstName || '',
    lastName: formValues.lastName || '',
    termOfUse: formValues.termOfUse || false,
    
    // Step 2
    companyName: formValues.companyName || '',
    industry: formValues.industry || '',
    website: formValues.website || '',
    
    // Step 3
    phone: formValues.phone || '',
    phone_verification: formValues.phone_verification || '',
    location: formValues.location || null,
    timezone: formValues.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    category: formValues.category || 'photography',
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (values: FormValues, { setSubmitting, setFieldValue, setFieldError }: any) => {
    setPhoneError(null);

    if (activeStep === 0) {
      // Step 0: Quick start or full form
      if (isQuickStart) {
        // Just save the website if provided and move to registration
        setFormValues({ ...formValues, ...values });
        setIsQuickStart(false);
        handleNext();
        setSubmitting(false);
      } else {
        // Handle field-by-field navigation
        if (currentField < 4) {
          // Just move to next field
          setFormValues({ ...formValues, ...values });
          setCurrentField(currentField + 1);
          setSubmitting(false);
        } else {
          // All fields complete, create account
          try {
            const cookies = parseCookies();
            const ref = cookies.ref || searchParams.get('ref');
            const accountInviteCookie = cookies.account_invite?.replace(
              /^"|"$/g,
              ''
            );
            const account_invite =
              accountInviteCookie || searchParams.get('account_invite');

            const registrationData = {
              firstName: values.firstName,
              lastName: values.lastName,
              email: values.email,
              password: values.password,
              // Phone will be required in step 3
              phone: '',
              phone_verification: '',
              termOfUse: values.termOfUse,
              ref,
              account_invite,
              timezone: values.timezone,
              category: values.category,
            };

            // Save form values for later steps
            setFormValues({ ...formValues, ...values });
            
            // For initial registration, we create the account with a placeholder phone
            // The actual phone will be updated in step 3
            const mappedData = PhotographMapper(registrationData);
            const response = await httpClient.post(API_ROUTES.registration, {
              user: mappedData,
            });

            if (response.data.access_token) {
              localStorage.setItem('user', JSON.stringify(response.data));
            }
            
            handleNext();
          } catch (error) {
            console.error('Registration error:', error);
            setFieldError('email', 'This email may already be registered');
          } finally {
            setSubmitting(false);
          }
        }
      }
    } else if (activeStep === 1) {
      // Step 2: Save company details
      const updatedValues = {
        ...values,
        website: values.website.startsWith('http') 
          ? values.website 
          : `https://${values.website}`,
      };
      setFormValues({ ...formValues, ...updatedValues });
      handleNext();
      setSubmitting(false);
    } else if (activeStep === 2) {
      // Step 3: Final submission with all data - Phone REQUIRED
      if (!values.phone) {
        setPhoneError('Phone number is required for registration');
        setSubmitting(false);
        return;
      }
      
      if (!isPhoneVerified) {
        setPhoneError('Please validate your phone number before submitting!');
        setSubmitting(false);
        return;
      }

      try {
        setLoading(true);
        
        // Combine all form data
        const finalData = {
          ...formValues,
          ...values,
          phone: values.phone,
          phone_verification: isPhoneVerified ? values.phone_verification : '',
        };

        // Update user profile with company and contact details
        const updateData = {
          user: {
            company_name: finalData.companyName,
            company_industry: finalData.industry,
            company_website: finalData.website.startsWith('http') 
              ? finalData.website 
              : `https://${finalData.website}`,
            contact_phone: finalData.phone,
            ...(finalData.location ? {
              company_address: finalData.location.label || finalData.location.description,
            } : {}),
          }
        };

        // Update the user profile on the server
        await UserService.updateUser(updateData);

        // Store in sessionStorage for plan selection
        sessionStorage.setItem('pendingRegistration', JSON.stringify(finalData));
        sessionStorage.setItem('userCompanyDetails', JSON.stringify({
          companyName: finalData.companyName,
          industry: finalData.industry,
          website: finalData.website,
        }));

        // Clear registration persistence data since registration is complete
        clearRegistrationData();

        // Redirect to plan selection
        window.location.href = '/select-plan';
      } catch (error) {
        console.error('Final submission error:', error);
        setFieldError('phone', 'Something went wrong. Please try again.');
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    }
  };

  return (
    <Box
      sx={{
        p: 0,
        borderRadius: { xs: 2, sm: 3 },
        background: theme.palette.background.paper,
        position: 'relative',
        overflow: 'visible',
        width: '100%',
        maxWidth: '100%',
        minHeight: 'auto',
      }}
    >
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        {/* Header with Title and Sign In Toggle */}
        <Box sx={{ textAlign: 'center', mb: { xs: 2, md: 3 } }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700, 
              mb: 1, 
              color: theme.palette.text.primary,
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' }
            }}>
            Create Your Account
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
            color="text.secondary">
            Already have an account?{' '}
            <Link
              component="button"
              variant="body1"
              onClick={onToggleSignIn}
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 600,
                textDecoration: 'none',
                fontSize: { xs: '0.875rem', md: '1rem' },
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Sign In
            </Link>
          </Typography>
        </Box>
        
      {/* Step Progress Indicator */}
      <Box sx={{ mb: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 1.5 }, mb: { xs: 1, md: 2 } }}>
          {STEP_TITLES.map((title, index) => {
            const isActive = index === activeStep;
            const isCompleted = index < activeStep;
            
            return (
              <React.Fragment key={index}>
                <Box
                  sx={{
                    flex: 1,
                    height: 4,
                    borderRadius: 2,
                    background: isCompleted ? theme.palette.primary.main : 
                               isActive ? `linear-gradient(90deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.3)})` :
                               alpha(theme.palette.action.disabled, 0.15),
                    transition: 'all 0.3s ease',
                    boxShadow: isActive ? `0 0 8px ${alpha(theme.palette.primary.main, 0.4)}` : 'none',
                  }}
                />
              </React.Fragment>
            );
          })}
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {STEP_TITLES.map((title, index) => {
            const isActive = index === activeStep;
            const isCompleted = index < activeStep;
            
            return (
              <Typography
                key={index}
                variant="caption"
                sx={{
                  color: isActive ? theme.palette.primary.main : 
                         isCompleted ? theme.palette.text.primary :
                         theme.palette.text.disabled,
                  fontWeight: isActive ? 700 : isCompleted ? 500 : 400,
                  fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.8rem' },
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  display: { xs: isActive ? 'block' : 'none', sm: 'block' },
                }}
              >
                {title}
              </Typography>
            );
          })}
        </Box>
      </Box>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchemas[activeStep]}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ values, errors, touched, handleChange, handleBlur, isSubmitting, setFieldValue, setFieldError }) => (
          <Form>
            <AnimatePresence>
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {activeStep === 0 && isQuickStart && (
                  <Grid container spacing={isMobile ? 1.5 : 2}>
                    <Grid item xs={12}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, textAlign: 'center' }}>
                        Start with your company website
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="medium"
                        name="website"
                        value={values.website}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Company Website (Optional)"
                        placeholder="e.g., www.yourcompany.com"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Language sx={{ color: theme.palette.action.active, fontSize: 20 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          or
                        </Typography>
                        <Button
                          variant="text"
                          size="large"
                          onClick={() => {
                            setIsQuickStart(false);
                            // Reset to show full form
                          }}
                          sx={{
                            color: theme.palette.primary.main,
                            textTransform: 'none',
                            fontSize: '1rem',
                            fontWeight: 500,
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          Sign up without website →
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                )}
                
                {activeStep === 0 && !isQuickStart && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                          {currentField === 0 && "What's your email?"}
                          {currentField === 1 && "What's your first name?"}
                          {currentField === 2 && "What's your last name?"}
                          {currentField === 3 && "Create a password"}
                          {currentField === 4 && "Terms of Service"}
                        </Typography>
                        
                        {currentField === 0 && (
                          <TextField
                            fullWidth
                            size="medium"
                            type="email"
                            name="email"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            label="Email Address"
                            placeholder="you@company.com"
                            error={Boolean(errors.email && touched.email)}
                            helperText={touched.email && errors.email}
                            autoFocus
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Email sx={{ color: theme.palette.action.active, fontSize: 20 }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={{ mb: 2 }}
                          />
                        )}
                        
                        {currentField === 1 && (
                          <TextField
                            fullWidth
                            size="medium"
                            name="firstName"
                            value={values.firstName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            label="First Name"
                            placeholder="John"
                            error={Boolean(errors.firstName && touched.firstName)}
                            helperText={touched.firstName && errors.firstName}
                            autoFocus
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Person sx={{ color: theme.palette.action.active, fontSize: 20 }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={{ mb: 2 }}
                          />
                        )}
                        
                        {currentField === 2 && (
                          <TextField
                            fullWidth
                            size="medium"
                            name="lastName"
                            value={values.lastName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            label="Last Name"
                            placeholder="Doe"
                            error={Boolean(errors.lastName && touched.lastName)}
                            helperText={touched.lastName && errors.lastName}
                            autoFocus
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Person sx={{ color: theme.palette.action.active, fontSize: 20 }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={{ mb: 2 }}
                          />
                        )}
                        
                        {currentField === 3 && (
                          <TextField
                            fullWidth
                            size="medium"
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            value={values.password}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            label="Password"
                            placeholder="Minimum 8 characters"
                            error={Boolean(errors.password && touched.password)}
                            helperText={touched.password && errors.password}
                            autoFocus
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Lock sx={{ color: theme.palette.action.active, fontSize: 20 }} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    onClick={() => setShowPassword(!showPassword)}
                                    edge="end"
                                  >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            sx={{ mb: 2 }}
                          />
                        )}
                        
                        {currentField === 4 && (
                          <Box sx={{ maxWidth: 400, margin: '0 auto' }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={values.termOfUse}
                                  onChange={handleChange}
                                  name="termOfUse"
                                  color="primary"
                                />
                              }
                              label={
                                <Typography variant="body2" sx={{ textAlign: 'left' }}>
                                  I agree to the{' '}
                                  <Link href="/terms" target="_blank" sx={{ color: theme.palette.primary.main }}>
                                    Terms of Service
                                  </Link>{' '}
                                  and{' '}
                                  <Link href="/privacy" target="_blank" sx={{ color: theme.palette.primary.main }}>
                                    Privacy Policy
                                  </Link>
                                </Typography>
                              }
                            />
                            {errors.termOfUse && touched.termOfUse && (
                              <Typography color="error" variant="caption">
                                {errors.termOfUse}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                )}

                {activeStep === 1 && (
                  <Grid container spacing={isMobile ? 1.5 : 2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        name="companyName"
                        value={values.companyName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Company Name"
                        placeholder="Your Business Name"
                        error={Boolean(errors.companyName && touched.companyName)}
                        helperText={touched.companyName && errors.companyName}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Business sx={{ color: theme.palette.action.active, fontSize: 18 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ mb: 1 }}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Autocomplete
                        options={SERVICE_INDUSTRIES}
                        value={values.industry}
                        onChange={(e, value) => setFieldValue('industry', value)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label="Industry"
                            placeholder="Select your industry"
                            error={Boolean(errors.industry && touched.industry)}
                            helperText={touched.industry && errors.industry}
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  <InputAdornment position="start">
                                    <Category sx={{ color: theme.palette.action.active, fontSize: 18 }} />
                                  </InputAdornment>
                                  {params.InputProps.startAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        sx={{ mb: 1 }}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        name="website"
                        value={values.website}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Website"
                        placeholder="www.yourcompany.com"
                        error={Boolean(errors.website && touched.website)}
                        helperText={touched.website && errors.website}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Language sx={{ color: theme.palette.action.active, fontSize: 18 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ mb: 1 }}
                      />
                    </Grid>
                  </Grid>
                )}

                {activeStep === 2 && (
                  <Grid container spacing={isMobile ? 1.5 : 2}>
                    <Grid item xs={12}>
                      <Box sx={{ 
                        p: 1.5, 
                        borderRadius: 1,
                        background: alpha(theme.palette.info.main, 0.05),
                        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                        mb: 2
                      }}>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem', color: theme.palette.info.main }}>
                          📱 Phone verification is required to complete registration
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12}>
                      {/* Show regular phone input when no phone is entered */}
                      {!values.phone ? (
                        <TextField
                          fullWidth
                          size="small"
                          type="tel"
                          name="phone"
                          value={values.phone}
                          onChange={handleChange}
                          label="Phone Number"
                          placeholder="+1234567890"
                          error={Boolean(errors.phone && touched.phone)}
                          helperText={touched.phone && errors.phone || "Required for account verification"}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Phone sx={{ color: theme.palette.action.active, fontSize: 18 }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{ mb: 2 }}
                        />
                      ) : (
                        /* Show phone verification component when phone is entered */
                        <Box sx={{ mb: 2 }}>
                          <PhoneVerification
                            initialPhoneNumber={values.phone}
                            existingUser={false}
                            email={formValues.email}
                            onPhoneNumberChange={(phone) => {
                              setFieldValue('phone', phone);
                              if (!phone || phone === '') {
                                setIsPhoneVerified(false);
                                setFieldValue('phone_verification', '');
                              }
                            }}
                            onPhoneVerification={(phoneNumber, token, isExistingUser) => {
                              if (isExistingUser) {
                                window.location.href = '/login';
                                return;
                              }
                              setIsPhoneVerified(true);
                              setFieldValue('phone_verification', token);
                            }}
                            onErrorsChange={(fieldName, error) => {
                              setFieldError(fieldName, error);
                            }}
                          />
                        </Box>
                      )}
                      {phoneError && values.phone && (
                        <Typography color="error" variant="caption" sx={{ mb: 1, display: 'block' }}>
                          {phoneError}
                        </Typography>
                      )}
                    </Grid>
                    
                    <Grid item xs={12}>
                      <GooglePlacesAutocomplete
                        apiKey={process.env.REACT_APP_GOOGLE_MAP_API_KEY}
                        selectProps={{
                          placeholder: "Location (Optional)",
                          value: values.location,
                          onChange: (place) => setFieldValue('location', place),
                          styles: {
                            control: (provided) => ({
                              ...provided,
                              minHeight: 40,
                              backgroundColor: theme.palette.background.paper,
                              borderColor: alpha(theme.palette.divider, 0.2),
                              fontSize: '0.875rem',
                            }),
                            input: (provided) => ({
                              ...provided,
                              color: theme.palette.text.primary,
                            }),
                            singleValue: (provided) => ({
                              ...provided,
                              color: theme.palette.text.primary,
                            }),
                            placeholder: (provided) => ({
                              ...provided,
                              color: theme.palette.text.secondary,
                            }),
                            menu: (provided) => ({
                              ...provided,
                              backgroundColor: theme.palette.background.paper,
                              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                            }),
                            option: (provided, { isFocused }) => ({
                              ...provided,
                              backgroundColor: isFocused 
                                ? alpha(theme.palette.primary.main, 0.1) 
                                : theme.palette.background.paper,
                              color: theme.palette.text.primary,
                            }),
                          },
                        }}
                      />
                    </Grid>
                    
                    {isPhoneVerified && values.phone && (
                      <Grid item xs={12}>
                        <Chip
                          icon={<CheckCircle />}
                          label="Phone Verified"
                          color="success"
                          size="small"
                          sx={{ mb: 1 }}
                        />
                      </Grid>
                    )}
                  </Grid>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Action Buttons */}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              {(activeStep > 0 || (!isQuickStart && currentField > 0)) && (
                <Button
                  onClick={() => {
                    if (!isQuickStart && activeStep === 0 && currentField > 0) {
                      setCurrentField(currentField - 1);
                    } else if (!isQuickStart && activeStep === 0 && currentField === 0) {
                      setIsQuickStart(true);
                      setCurrentField(0);
                    } else {
                      handleBack();
                    }
                  }}
                  startIcon={<ArrowBack />}
                  variant="outlined"
                  size="small"
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    px: { xs: 1.5, sm: 2, md: 3 },
                    fontSize: { xs: '0.875rem', sm: '0.875rem', md: '1rem' },
                  }}
                >
                  Back
                </Button>
              )}
              
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting || loading}
                endIcon={activeStep === 0 && !isQuickStart && currentField < 4 ? <ArrowForward /> : 
                        activeStep < 2 ? <ArrowForward /> : <Check />}
                size="small"
                fullWidth={activeStep === 0 && (isQuickStart || currentField === 0)}
                sx={{
                  ml: (activeStep === 0 && (isQuickStart || currentField === 0)) ? 0 : 'auto',
                  borderRadius: 2,
                  textTransform: 'none',
                  px: { xs: 2, sm: 3, md: 4 },
                  fontSize: { xs: '0.875rem', sm: '0.875rem', md: '1rem' },
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  },
                }}
              >
                {activeStep === 2 ? 
                  (values.phone && !isPhoneVerified ? 'Verify Phone Number' : 'Complete Registration') : 
                 activeStep === 0 && !isQuickStart && currentField === 4 ? 'Create Account' : 'Continue'}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
    </Box>
  );
};

export default MultiStepRegistration;